import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { getCurrentUser } from '@/services/userService'
import type { ProfileSettings } from '@/types'

export type UserRole = 'CANDIDATE' | 'RECRUITER' | 'ADMIN'

export interface User {
  id: string
  email: string
  role: UserRole
  emailVerified: boolean
  dataProcessingConsent?: boolean // Whether user has given consent for data processing
  consentVersion?: string // Version of consent accepted (e.g., "1.0")
  consentGivenAt?: string // ISO timestamp when consent was given
  firstName?: string
  lastName?: string
  profilePhoto?: string
  credits?: number
  username?: string
  profileSettings?: ProfileSettings
}

// Helper to validate user object has required fields
export function isValidUser(user: any): user is User {
  return (
    user &&
    typeof user === 'object' &&
    typeof user.id === 'string' &&
    typeof user.email === 'string' &&
    typeof user.role === 'string' &&
    typeof user.emailVerified === 'boolean'
  )
}

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  _hasHydrated: boolean

  // Actions
  setAuth: (user: User, accessToken: string, refreshToken: string) => void
  updateAccessToken: (accessToken: string) => void
  updateUser: (user: User) => void
  logout: () => void
  setHasHydrated: (hasHydrated: boolean) => void
  refreshUser: () => Promise<void>
}

export const useAuth = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      _hasHydrated: false,

      setAuth: (user, accessToken, refreshToken) => {
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
        })
      },

      updateAccessToken: (accessToken) => {
        set({ accessToken })
      },

      updateUser: (user) => {
        set({ user })
      },

      logout: () => {
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        })
      },

      setHasHydrated: (hasHydrated) => {
        set({ _hasHydrated: hasHydrated })
      },

      refreshUser: async () => {
        try {
          const freshUser = await getCurrentUser()
          set({ user: freshUser })
        } catch {
          // Silently fail - user will be logged out on next API call if token expired
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.setHasHydrated(true)
        }
      },
    }
  )
)
